import React, { useCallback, useEffect, useState } from 'react';

import Image from '@atlaskit/image/image';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { defineMessage, useIntl } from 'react-intl';

import { useAnalytics } from '@townsquare/analytics';
import { HomeScreenSpaceFlagsChangeBoarding } from '@townsquare/post-office/placements';

import { useFinesseFeedbackModal } from './FinesseFeedbackCollector';
import finesseFeedbackBanner from './assets/finesse-feedback-banner.png';

const FINESSE_FEEDBACK_FEATURE_ID = 'home-finesse-feedback-spotlight';

const bannerAltMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.banner-alt',
	defaultMessage: 'Updated navigation selection and checkbox styles.',
	description: 'Alternative text for the illustration comparing old and new visual styles.',
});

const headlineMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.headline',
	defaultMessage: 'Help us improve visual clarity',
	description: 'Headline for the spotlight asking users for feedback on visual hierarchy changes.',
});

const bodyMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.body',
	defaultMessage:
		'We’ve updated the color and contrast of several UI elements. Tell us what feels clearer or more difficult to use.',
	description: 'Body copy for the spotlight asking users for feedback on visual hierarchy changes.',
});

const primaryActionMessage = defineMessage({
	id: 'townsquare.home.finesse-feedback-spotlight.primary-action',
	defaultMessage: 'Give feedback',
	description: 'Label for the action that opens the visual hierarchy feedback collector.',
});

interface FinesseFeedbackSpotlightContentProps {
	onDismiss: () => void;
}

const FinesseFeedbackSpotlightContent = ({
	onDismiss,
}: FinesseFeedbackSpotlightContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const analytics = useAnalytics();
	const [isVisible, setIsVisible] = useState(true);
	const { openFinesseFeedbackModal } = useFinesseFeedbackModal();

	useEffect(() => {
		void analytics.ui('finesseFeedbackSpotlight', 'rendered');
	}, [analytics]);

	const dismiss = useCallback(
		(source: 'close' | 'outside') => {
			setIsVisible(false);
			onDismiss();
			void analytics.ui('finesseFeedbackSpotlight', 'dismissed', { source });
		},
		[analytics, onDismiss],
	);

	const openFeedback = useCallback(() => {
		setIsVisible(false);
		onDismiss();
		void analytics.ui('finesseFeedbackSpotlight', 'clicked');
		openFinesseFeedbackModal();
	}, [analytics, onDismiss, openFinesseFeedbackModal]);

	return (
		<PopoverContent dismiss={() => dismiss('outside')} isVisible={isVisible} placement="right-end">
			<SpotlightCard testId="finesse-feedback-spotlight">
				<SpotlightHeader>
					<SpotlightHeadline>{formatMessage(headlineMessage)}</SpotlightHeadline>
					<SpotlightControls>
						<SpotlightDismissControl autoFocus={false} onClick={() => dismiss('close')} />
					</SpotlightControls>
				</SpotlightHeader>
				<SpotlightMedia>
					<Image
						src={finesseFeedbackBanner}
						alt={formatMessage(bannerAltMessage)}
						width={295}
						height={135}
					/>
				</SpotlightMedia>
				<SpotlightBody>{formatMessage(bodyMessage)}</SpotlightBody>
				<SpotlightFooter>
					<SpotlightActions>
						<SpotlightPrimaryAction onClick={openFeedback}>
							{formatMessage(primaryActionMessage)}
						</SpotlightPrimaryAction>
					</SpotlightActions>
				</SpotlightFooter>
			</SpotlightCard>
		</PopoverContent>
	);
};

export function FinesseFeedbackSpotlight({ children }: React.PropsWithChildren): React.JSX.Element {
	if (!fg('platform-dst-tokens-finesse')) {
		return <>{children}</>;
	}

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<HomeScreenSpaceFlagsChangeBoarding featureId={FINESSE_FEEDBACK_FEATURE_ID}>
				{({ onDismiss }) => <FinesseFeedbackSpotlightContent onDismiss={onDismiss} />}
			</HomeScreenSpaceFlagsChangeBoarding>
		</PopoverProvider>
	);
}
